import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import UserPatternSettingUpdate from "./_111_user_pattern_setting_update.js";
import { useDateRange } from "./dateRangeContext.js";
import { formatDateMMDDYY } from "./patterns/helpers.js";
import { logEvent } from "./eventLogger.js";
import { getCachedPatternData, setCachedPatternData } from "./patterns/cacheHelper.js"; // ✅ NEW
import "./styles.css";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { Link } from "react-router-dom";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

const MODULE_NAME = "92_pattern_symbol_attachment";

// --- Settings Icon SVG ---
const SettingsIcon = ({ size = 28, color = "#fff" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 28 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ verticalAlign: "middle", cursor: "pointer" }}
    >
        <circle cx="14" cy="14" r="13" stroke={color} strokeWidth="2" fill="none" />
        <rect x="9" y="13" width="10" height="2" rx="1" fill={color} />
        <rect x="13" y="9" width="2" height="10" rx="1" fill={color} />
    </svg>
);

// --- Gear Icon Unicode ---
const GearIcon = ({ size = 24, color = "#fff" }) => (
    <span
        style={{
            fontSize: size,
            color,
            verticalAlign: "middle",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1,
            display: "inline-block",
        }}
        title="Settings"
        aria-label="Settings"
    >
        &#9881;
    </span>
);

// --- Table Styles ---
const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: 16,
    background: "#222",
};

const thStyle = {
    padding: "8px 12px",
    fontSize: 13,
    textAlign: "left",
    borderBottom: "1px solid #555",
    backgroundColor: "#333",
    fontWeight: 600,
    color: "#fff",
};

const tdStyle = {
    padding: "8px 12px",
    borderBottom: "1px solid #444",
    fontSize: 13,
    color: "#ccc",
};

const Pattern_symbol_attachment = ({ userId: propUserId, onNavigate: propOnNavigate }) => {
    const { windowDays } = useDateRange();

    // Derived userId with fallback
    const storedId = sessionStorage.getItem("user_id");
    const userId = propUserId || (storedId && !isNaN(Number(storedId)) ? Number(storedId) : 1);

    const [date, setDate] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [symbolAttachmentPattern, setSymbolAttachmentPattern] = useState(null);
    const [totalPL, setTotalPL] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [chartMode, setChartMode] = useState("cumulative");
    const [showSettings, setShowSettings] = useState(false);
    const [patternId, setPatternId] = useState(null);
    const [patternConfig, setPatternConfig] = useState(null);
    const [showTable, setShowTable] = useState(false);
    const [fromCache, setFromCache] = useState(false); // ✅ Track cache status
    const chartContainerRef = useRef(null);

    // Helper to parse windowDays to a valid lookback number
    function getLookbackDays(val) {
        if (!val) return 30;
        if (!isNaN(Number(val))) return Number(val);
        if (val === "YTD") return 365;
        return 30;
    }

    // ✅ UPDATED: Fetch data with async database cache
    const fetchPatternData = async (days) => {
        setLoading(true);
        
        try {
            // 🔓 NEW: Retrieve userKey from sessionStorage for decryption
            const userKey = sessionStorage.getItem("userKey");
            const fetchParams = { 
                lookback_days: days 
            };

            // Inject userKey if Privacy Mode is active
            if (userKey) {
                fetchParams.userKey = userKey;
            }

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/_92_pattern_symbol_attachment`,
                {
                    params: fetchParams,
                    withCredentials: true,
                }
            );
            
            setDate(res.data.date);
            setTooltip(res.data.tooltip);
            setSymbolAttachmentPattern(res.data);
            setTotalPL(res.data.total_pl);

            // ✅ UPDATED: Extract numeric pattern_id from API response
            const numericPatternId = res.data.pattern_id;
            setPatternId(numericPatternId);
            setPatternConfig(res.data.config || {});

            // ✅ UPDATED: Use numeric pattern_id as cache key
            if (numericPatternId) {
                await setCachedPatternData(
                    `pattern_${numericPatternId}`,
                    days,
                    res.data
                );
            }

            console.log("Pattern ID:", numericPatternId, "Config:", res.data.config);
            setFromCache(false);
        } catch (err) {
            console.error("Error fetching pattern data:", err);
            
            // ✅ Try to load from cache on error
            if (patternId) {
                const cachedData = await getCachedPatternData(
                    `pattern_${patternId}`,
                    days
                );
                if (cachedData) {
                    setDate(cachedData.date);
                    setTooltip(cachedData.tooltip);
                    setSymbolAttachmentPattern(cachedData);
                    setTotalPL(cachedData.total_pl);
                    setPatternConfig(cachedData.config || {});
                    setFromCache(true);
                    setLoading(false);
                    return;
                }
            }
            
            setTooltip("Error fetching data.");
            setSymbolAttachmentPattern(null);
            setTotalPL(null);
            setPatternId(null);
        }
        
        setLoading(false);
    };

    useEffect(() => {
        const lookback = getLookbackDays(windowDays);
        if (lookback) {
            fetchPatternData(lookback);
        }
        // eslint-disable-next-line
    }, [windowDays]);

    // --- Settings Modal ---
    const renderSettingsModal = () => {
        if (!showSettings || !patternId) return null;

        return (
            <>
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(24,26,37,0.5)",
                        backdropFilter: "blur(6px)",
                        zIndex: 9998
                    }}
                    onClick={() => setShowSettings(false)}
                />
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9999,
                        minWidth: 600,
                        minHeight: 420,
                        background: "#181A25",
                        borderRadius: 12,
                        boxShadow: "0 2px 8px rgba(0,255,255,0.08)",
                        padding: 0
                    }}
                >
                    <UserPatternSettingUpdate
                        patternId={patternId}
                        countThreshold={patternConfig?.count_threshold}
                        thresholdDefinition={patternConfig?.threshold_definition}
                    />
                    <button
                        onClick={() => setShowSettings(false)}
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 18,
                            background: "none",
                            border: "none",
                            color: "#fff",
                            fontSize: 22,
                            cursor: "pointer"
                        }}
                        title="Close"
                    >✕</button>
                </div>
            </>
        );
    };

    // Chart data preparation
    const getChartData = () => {
        if (!symbolAttachmentPattern || !Array.isArray(symbolAttachmentPattern.time_evolution))
            return [];
        if (chartMode === "cumulative") {
            return symbolAttachmentPattern.time_evolution;
        }
        if (chartMode === "rolling") {
            // 5-day rolling average for P/L and win rate
            const arr = symbolAttachmentPattern.time_evolution;
            const window = 5;
            return arr.map((d, i) => {
                const slice = arr.slice(Math.max(0, i - window + 1), i + 1);
                const avgPL =
                    slice.reduce((sum, x) => sum + (x.daily_pl || 0), 0) / slice.length;
                const avgWin =
                    (slice.reduce((sum, x) => sum + (x.daily_wins || 0), 0) *
                        100) /
                    (slice.reduce((sum, x) => sum + (x.daily_trades || 0), 0) || 1);
                const avgFreq =
                    slice.reduce((sum, x) => sum + (x.frequency || 0), 0) / slice.length;
                return {
                    ...d,
                    rolling_pl: avgPL,
                    rolling_win_rate: avgWin,
                    rolling_frequency: avgFreq,
                };
            });
        }
        if (chartMode === "scatter" && Array.isArray(symbolAttachmentPattern.pl_matches)) {
            // Each trade as a point
            return symbolAttachmentPattern.pl_matches.map((d, i) => ({
                ...d,
                index: i + 1,
            }));
        }
        return [];
    };

    // Helper to get chart labels in MM/DD/YY format
    const getChartLabels = (data) =>
        data.map((d) => formatDateMMDDYY(d.date || d.trade_date));

    // Prepare Chart.js datasets
    const buildChartData = () => {
        const data = getChartData();
        if (!data.length) return { labels: [], datasets: [] };

        if (chartMode === "scatter") {
            return {
                labels: getChartLabels(data),
                datasets: [
                    {
                        type: "scatter",
                        label: "Trade P/L",
                        data: data.map((d, idx) => ({
                            x: idx,
                            y: d.pl,
                        })),
                        backgroundColor: data.map((d) =>
                            d.pl >= 0 ? "rgba(76, 175, 80, 0.6)" : "rgba(244, 67, 54, 0.6)"
                        ),
                        pointRadius: 6,
                        yAxisID: "y",
                    },
                ],
            };
        }

        // For cumulative/rolling
        const plKey = chartMode === "rolling" ? "rolling_pl" : "cumulative_pl";
        const winKey = chartMode === "rolling" ? "rolling_win_rate" : "cumulative_win_rate";
        const freqKey = chartMode === "rolling" ? "rolling_frequency" : "frequency";
        const winsKey = chartMode === "rolling" ? "rolling_wins" : "daily_wins";

        // Calculate wins and losses for each data point
        const wins = data.map(d => d[winsKey] || 0);
        const freq = data.map(d => d[freqKey] || 0);
        const losses = freq.map((f, i) => Math.max(0, f - wins[i]));

        return {
            labels: getChartLabels(data),
            datasets:
                [
                    {
                        type: "line",
                        label: "P/L",
                        data: data.map((d) => d[plKey]),
                        borderColor: "#1976d2",
                        backgroundColor: "rgba(25, 118, 210, 0.1)",
                        borderWidth: 3,
                        pointRadius: 3,
                        fill: true,
                        tension: 0.3,
                        yAxisID: "y",
                    },
                    {
                        type: "line",
                        label: "Win Rate %",
                        data: data.map((d) => d[winKey]),
                        borderColor: "#4caf50",
                        backgroundColor: "rgba(76, 175, 80, 0.1)",
                        borderWidth: 2,
                        pointRadius: 2,
                        fill: true,
                        tension: 0.3,
                        yAxisID: "y1",
                    },
                    {
                        type: "bar",
                        label: "Wins",
                        data: wins,
                        backgroundColor: "rgba(76, 175, 80, 0.6)",
                        borderColor: "#4caf50",
                        borderWidth: 1,
                        yAxisID: "y2",
                        stack: "frequency",
                    },
                    {
                        type: "bar",
                        label: "Losses",
                        data: losses,
                        backgroundColor: "rgba(244, 67, 54, 0.6)",
                        borderColor: "#F44336",
                        borderWidth: 1,
                        yAxisID: "y2",
                        stack: "frequency",
                    },
                ],
        };
    };

    const chartData = buildChartData();
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: "top" },
            title: { display: false },
        },
        scales: {
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "P/L ($)" },
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
            y1: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Win Rate (%)" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
            },
            y2: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Frequency" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
                offset: true,
            },
            x: {
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
        },
    };

    // --- Data Table ---
    const renderDataTable = () => {
        if (!symbolAttachmentPattern || !Array.isArray(symbolAttachmentPattern.pl_matches) || symbolAttachmentPattern.pl_matches.length === 0) {
            return <div style={{ color: "#888", padding: 16 }}>No trade data available.</div>;
        }

        const trades = symbolAttachmentPattern.pl_matches;

        // Group trades by symbol and trade_date
        const groupedBySymbolDate = {};
        trades.forEach((trade) => {
            const key = `${trade.symbol}_${trade.trade_date}`;
            if (!groupedBySymbolDate[key]) {
                groupedBySymbolDate[key] = {
                    symbol: trade.symbol,
                    trade_date: trade.trade_date,
                    pl: 0,
                    count: 0,
                };
            }
            groupedBySymbolDate[key].pl += trade.pl;
            groupedBySymbolDate[key].count += 1;
        });

        // Convert to array and sort
        const summarizedTrades = Object.values(groupedBySymbolDate)
            .sort((a, b) => {
                if (a.symbol !== b.symbol) {
                    return a.symbol.localeCompare(b.symbol);
                }
                return new Date(b.trade_date) - new Date(a.trade_date);
            });

        // Group by symbol
        const tradesBySymbol = {};
        summarizedTrades.forEach((trade) => {
            if (!tradesBySymbol[trade.symbol]) {
                tradesBySymbol[trade.symbol] = [];
            }
            tradesBySymbol[trade.symbol].push(trade);
        });

        return (
            <div style={{ marginTop: 16 }}>
                <div style={{ marginBottom: 12 }}>
                    <button
                        onClick={() => setShowTable(!showTable)}
                        style={{
                            padding: "8px 16px",
                            borderRadius: 6,
                            border: "1px solid #888",
                            background: showTable ? "#1976d2" : "#333",
                            color: "#fff",
                            fontWeight: 500,
                            cursor: "pointer",
                            transition: "background 0.2s",
                            fontSize: "0.98em",
                        }}
                    >
                        {showTable ? "Hide Transaction Summary" : "Show Transaction Summary"} ({summarizedTrades.length} days)
                    </button>
                </div>

                {showTable && (
                    <div style={{ overflowX: "auto" }}>
                        {Object.entries(tradesBySymbol).map(([symbol, symbolTrades]) => (
                            <div key={symbol} style={{ marginBottom: 20 }}>
                                {/* Symbol Header */}
                                <div style={{
                                    padding: "6px 12px",
                                    background: "#2a3f5f",
                                    borderRadius: "4px 4px 0 0",
                                    fontWeight: 600,
                                    color: "#4fa3f7",
                                    fontSize: 13,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                }}>
                                    <span>{symbol}</span>
                                    <span style={{ fontSize: 12, color: "#aaa", fontWeight: 400 }}>
                                        {symbolTrades.length} trading days
                                    </span>
                                </div>

                                {/* Symbol Transactions Table */}
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Trade Date</th>
                                            <th style={thStyle}>Daily P/L</th>
                                            <th style={thStyle}>Trades</th>
                                            <th style={thStyle}>View Tags</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {symbolTrades.map((trade, idx) => {
                                            const tradeDate = trade.trade_date;
                                            const isoDate = /^\d{4}-\d{2}-\d{2}$/.test(tradeDate)
                                                ? tradeDate
                                                : `${tradeDate.split("/").reverse().join("-")}`;

                                            return (
                                                <tr key={idx}>
                                                    <td style={tdStyle}>
                                                        {formatDateMMDDYY(trade.trade_date)}
                                                    </td>
                                                    <td style={{
                                                        ...tdStyle,
                                                        color: trade.pl >= 0 ? "#4caf50" : "#e53935",
                                                        fontWeight: 600,
                                                    }}>
                                                        ${trade.pl.toLocaleString(undefined, {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </td>
                                                    <td style={tdStyle}>
                                                        {trade.count}
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <Link
                                                            to={`/pg_details?date=${isoDate}&symbol=${symbol}`}
                                                            style={{
                                                                color: "#0077cc",
                                                                textDecoration: "underline",
                                                                cursor: "pointer",
                                                                fontSize: "0.98em"
                                                            }}
                                                            title={`View tags for ${symbol} on ${formatDateMMDDYY(trade.trade_date)}`}
                                                            onClick={() => {
                                                                sessionStorage.setItem("navigationDate", isoDate);
                                                                sessionStorage.setItem("navigationSymbol", symbol);
                                                                logEvent({
                                                                    event_type: "button_click",
                                                                    user_id: userId,
                                                                    module_name: MODULE_NAME,
                                                                    subcomponent: "View Tags Link",
                                                                    screen: window.location.pathname,
                                                                    metadata: {
                                                                        symbol,
                                                                        trade_date: isoDate,
                                                                        button_link_value: "Tag details"
                                                                    }
                                                                });
                                                                if (propOnNavigate) {
                                                                    propOnNavigate("pg_details", isoDate, symbol);
                                                                }
                                                            }}
                                                        >
                                                            Tag details
                                                        </Link>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    {/* Symbol Total */}
                                    <tfoot>
                                        <tr>
                                            <td style={{
                                                ...tdStyle,
                                                fontWeight: 600,
                                                backgroundColor: "#2a2d38",
                                                textAlign: "right"
                                            }}>
                                                Total:
                                            </td>
                                            <td style={{
                                                ...tdStyle,
                                                fontWeight: 600,
                                                backgroundColor: "#2a2d38",
                                                color: symbolTrades.reduce((sum, t) => sum + t.pl, 0) >= 0 ? "#4caf50" : "#e53935",
                                            }}>
                                                ${symbolTrades.reduce((sum, t) => sum + t.pl, 0).toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td style={{
                                                ...tdStyle,
                                                fontWeight: 600,
                                                backgroundColor: "#2a2d38",
                                            }}>
                                                {symbolTrades.reduce((sum, t) => sum + t.count, 0)}
                                            </td>
                                            <td style={{
                                                ...tdStyle,
                                                backgroundColor: "#2a2d38",
                                            }}>
                                                —
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Pattern label/meaning and details
    const renderPatternMessages = () => {
        if (
            !symbolAttachmentPattern ||
            !Array.isArray(symbolAttachmentPattern.details) ||
            symbolAttachmentPattern.details.length === 0
        ) {
            return <span>No data available.</span>;
        }

        let label = symbolAttachmentPattern.pattern_label;
        let description = symbolAttachmentPattern.pattern_meaning;

        if (
            (!label || !description) &&
            symbolAttachmentPattern.config &&
            symbolAttachmentPattern.config.count_threshold
        ) {
            const thresholds = symbolAttachmentPattern.config.count_threshold;
            if (thresholds.emerging) {
                label = thresholds.emerging.label;
                description = thresholds.emerging.description;
            }
        }

        const symbolMap = {};
        symbolAttachmentPattern.details.forEach((item) => {
            if (!symbolMap[item.symbol] || item.streak_length > symbolMap[item.symbol]) {
                symbolMap[item.symbol] = item.streak_length;
            }
        });

        const groupedDetails = Object.entries(symbolMap)
            .slice(0, 10)
            .map(([symbol, streak_length]) => ({ symbol, streak_length }));

        // Format min_price_range_percentage as a friendly percent
        let minPriceRangePct = symbolAttachmentPattern.min_price_range_percentage;
        let minPriceRangePctDisplay = "";
        if (typeof minPriceRangePct === "number" && !isNaN(minPriceRangePct)) {
            minPriceRangePctDisplay = `Catalyst threshold: ${minPriceRangePct.toLocaleString(undefined, { maximumFractionDigits: 2 })}% price range`;
        }

        const userTimezone = symbolAttachmentPattern.user_timezone || "";
        const convertTimezone = symbolAttachmentPattern.convert_timezone ? "Yes" : "No";

        const moreDetails = (
            <div style={{ margin: "8px 0" }}>
                <a
                    href="#"
                    style={{
                        color: "#4fa3f7",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "0.97em",
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        setShowDetails((prev) => !prev);
                    }}
                >
                    {showDetails ? "Hide details" : "More details"}
                </a>
                {showDetails && (
                    <div
                        style={{
                            marginTop: 6,
                            color: "#aaa",
                            fontSize: "0.97em",
                        }}
                    >
                        {minPriceRangePctDisplay && (
                            <div>{minPriceRangePctDisplay}</div>
                        )}
                        <div>
                            User preferred timezone:{" "}
                            <b>{userTimezone}</b> | Convert timezone: <b>{convertTimezone}</b>
                        </div>
                        {/* ✅ NEW: Show cache status */}
                        {fromCache && (
                            <div style={{ marginTop: 6, fontSize: "0.9em", color: "#888" }}>
                                📦 Data loaded from database cache (4 hour TTL)
                            </div>
                        )}
                    </div>
                )}
            </div>
        );

        return (
            <div style={{ marginBottom: 8 }}>
                <span>
                    {label || "Emerging Pattern"}: {description}
                </span>
                {moreDetails}
                {groupedDetails.map((item, idx) => (
                    <div key={idx}>
                        {item.symbol}: streak length {item.streak_length}
                    </div>
                ))}
            </div>
        );
    };

    // Chart mode toggles
    const renderChartToggles = () => (
        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
            {["cumulative", "rolling", "scatter"].map((mode) => {
                const buttonLabels = {
                    cumulative: "Cumulative",
                    rolling: "Rolling Avg",
                    scatter: "Trade Scatter"
                };
                const buttonLabel = buttonLabels[mode];
                const subcomponentLabel = mode === "cumulative" 
                    ? "Cumulative Chart" 
                    : mode === "rolling" 
                    ? "Rolling Avg Chart" 
                    : "Trade Scatter Chart";

                return (
                    <div key={mode} data-analytics={MODULE_NAME}>
                        <button
                            className={`btn ${chartMode === mode ? "btn-selected" : ""}`}
                            onClick={() => {
                                logEvent({
                                    event_type: "button_click",
                                    user_id: userId,
                                    module_name: MODULE_NAME,
                                    subcomponent: subcomponentLabel,
                                    screen: window.location.pathname,
                                    metadata: {
                                        button_link_value: buttonLabel
                                    }
                                });
                                setChartMode(mode);
                            }}
                            style={{
                                padding: "6px 14px",
                                borderRadius: 6,
                                border: "1px solid #888",
                                background: chartMode === mode ? "#1976d2" : "#f5f5f5",
                                color: chartMode === mode ? "#fff" : "#222",
                                fontWeight: 500,
                                cursor: "pointer",
                                transition: "background 0.2s",
                                fontSize: "0.98em",
                            }}
                        >
                            {buttonLabel}
                        </button>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div
            ref={chartContainerRef}
            style={{
                width: "100%",
                background: "none",
                borderRadius: 0,
                padding: 0,
                margin: 0,
                maxWidth: "100%",
                position: "relative"
            }}
            data-analytics={MODULE_NAME}
        >
            <h3
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: "1.25em",
                    margin: "0 0 8px 0",
                    color: "#fff",
                }}
            >
                {symbolAttachmentPattern?.pattern_name || "Symbol Attachment"}{" "}
                {tooltip && (
                    <span
                        className="tooltip-icon"
                        style={{
                            marginLeft: 6,
                            verticalAlign: "middle",
                            cursor: "pointer",
                            fontSize: "1.1em",
                        }}
                        title={tooltip}
                    >
                        🛈
                    </span>
                )}
                <span
                    style={{ marginLeft: 8 }}
                    title="Edit pattern settings"
                    onClick={() => {
                        logEvent({
                            event_type: "button_click",
                            user_id: userId,
                            module_name: MODULE_NAME,
                            subcomponent: "Settings Gear",
                            screen: window.location.pathname,
                            metadata: { button_link_value: "Settings Gear" }
                        });
                        setShowSettings(true);
                    }}
                >
                    <GearIcon size={24} color="#fff" />
                </span>
            </h3>
            {renderSettingsModal()}
            <div style={{ minHeight: 32, fontSize: "1.05em", marginBottom: 16 }}>
                {loading ? (
                    <span>Loading...</span>
                ) : (
                    <>
                        <span style={{ color: "#888", fontSize: "0.95em" }}>
                            
                        </span>
                        {typeof totalPL === "number" && (
                            <div
                                style={{
                                    marginTop: 6,
                                    color: "#e0b400",
                                    fontSize: "1.1em",
                                }}
                            >
                                <b>P/L:</b>{" "}
                                <span
                                    style={{
                                        color: totalPL >= 0 ? "#4caf50" : "#e53935",
                                    }}
                                >
                                    ${totalPL.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </span>
                                {/* Cumulative Win Rate % below P/L */}
                                {symbolAttachmentPattern?.cumulative_win_rate !== undefined && (
                                    <div style={{ marginTop: 2, color: "#4caf50", fontSize: "1em" }}>
                                        <b>Cumulative Win %:</b>{" "}
                                        {symbolAttachmentPattern.cumulative_win_rate.toFixed(2)}%
                                    </div>
                                )}
                            </div>
                        )}
                        <div style={{ marginTop: 8 }}>{renderPatternMessages()}</div>
                        <div style={{ marginTop: 10 }}>
                            {renderChartToggles()}
                            <div style={{ position: "relative", width: "100%", minHeight: 320, maxWidth: "100%" }}>
                                {chartData.datasets.length > 0 ? (
                                    <Chart
                                        type="bar"
                                        data={chartData}
                                        options={{
                                            ...chartOptions,
                                            responsive: true,
                                            maintainAspectRatio: false,
                                        }}
                                    />
                                ) : (
                                    <div style={{ color: "#888", padding: "20px", textAlign: "center" }}>
                                        No chart data available for this period.
                                    </div>
                                )}
                            </div>
                            {renderDataTable()}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Pattern_symbol_attachment;